import React, { useEffect, useState } from "react";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { format } from "date-fns";
import { getChronologicleDay } from "../getChronologicleDay"; // adjust path if needed
import { motion } from "framer-motion";
import './Leaderboard.css';
import { getGuessCountLabel } from "../gameSession";

// Chevron SVG component
const ChevronDown = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const calculateAverage = (players) =>
  players.length > 0
    ? Math.round(players.reduce((sum, player) => sum + player.score, 0) / players.length)
    : null;

const Leaderboard = ({
  dateKey = format(new Date(), "yyyy-MM-dd"),
  currentUid = null,
  onReturn = null,
  setMenuClicked = () => { }, // ✅ default no-op if not passed
  testData = null,
  subscribe = onSnapshot,
  loadGameDay = getChronologicleDay,
}) => {
  const [players, setPlayers] = useState(() => testData?.players ?? []);
  const [average, setAverage] = useState(() => testData ? (testData.average ?? calculateAverage(testData.players ?? [])) : null);
  const [loading, setLoading] = useState(() => testData?.loading ?? !testData);
  const [loadError, setLoadError] = useState(() => testData?.error ?? null);
  const [retryCount, setRetryCount] = useState(0);
  const [gameDay, setGameDay] = useState(() => testData?.gameDay ?? null);

  useEffect(() => {
    if (testData) return undefined;
    loadGameDay().then((day) => setGameDay(day));
  }, [loadGameDay, testData]);

  useEffect(() => {
    if (testData) return undefined;

    setLoading(true);
    setLoadError(null);

    const playersRef = collection(db, "leaderboards", dateKey, "players");
    const q = query(playersRef, orderBy("score", "desc"), limit(100));

    const unsubscribe = subscribe(q, (snapshot) => {
      let results = snapshot.docs.map(doc => doc.data());

      // 1. Sort: Score DESC, then Name ASC
      results.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB);
      });

      // 2. Assign Ranks
      for (let i = 0; i < results.length; i++) {
        if (i > 0 && results[i].score === results[i - 1].score) {
          // Tie found
          const baseRank = results[i - 1].rank.toString().replace("T", "");
          results[i].rank = `T${baseRank}`;
          results[i - 1].rank = `T${baseRank}`; // Ensure previous gets T
        } else {
          // New rank
          results[i].rank = (i + 1).toString();
        }
      }

      setPlayers(results);

      if (results.length > 0) {
        const avg = Math.round(results.reduce((sum, p) => sum + p.score, 0) / results.length);
        setAverage(avg);
      } else {
        setAverage(null);
      }

      setLoading(false);
    }, (error) => {
      console.warn("Leaderboard subscription failed", error?.code || "unknown");
      setLoadError("We couldn’t load today’s leaderboard.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [dateKey, retryCount, subscribe, testData]);

  useEffect(() => {
    setMenuClicked(true); // ✅ hide top-right profile and help button
  }, [setMenuClicked]);

  const [expandedPlayer, setExpandedPlayer] = useState(() => testData?.expandedPlayer ?? null);


  return (
    <div
      style={{
        maxWidth: "800px",
        margin: "0 auto",
        height: "100dvh", // fixed height
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        padding: "0", // Remove padding from outer container
        color: "#e7c17f",
        fontFamily: "'Baskerville', serif",
        fontWeight: "bold",
        boxSizing: "border-box", // Ensure padding is included in height
        overflow: "hidden", // Prevent full page scroll
        backgroundColor: "#034c39", // Match MyStatsPage background
      }}
    >
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "2rem",
        padding: "0 1.5rem",
        position: "relative",
        minHeight: "3rem",
        marginTop: "1.5rem" // added to match MyStats container padding
      }}>
        <button
          onClick={onReturn}
          aria-label="Return from leaderboard"
          style={{
            position: "fixed", // Keep fixed
            top: "1.5rem",
            left: "1.5rem",
            background: "none",
            border: "none",
            color: "#e7c17f",
            fontSize: "1.8rem",
            cursor: "pointer",
            padding: "0.5rem",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <h1 style={{ margin: 0, fontSize: "2.4rem", textAlign: "center" }}>
            Leaderboard
          </h1>
          {average !== null && (
            <p style={{ fontWeight: "100", textAlign: "center", marginTop: "0.5rem", fontSize: "1rem" }}>
              Game No. {gameDay ?? ".."} | {dateKey} | Avg: {average}
            </p>
          )}
        </div>
      </div>

      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "0 1.5rem 2rem 1.5rem", // Match MyStats scrollable-content padding
        width: "100%",
        boxSizing: "border-box"
      }}>

        {loading ? (
          <div style={{
            position: "fixed",
            top: -40,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}>
            <p role="status" aria-live="polite" style={{ fontSize: "1.2rem" }}>Loading leaderboard…</p>
          </div>
        ) : loadError ? (
          <div
            role="alert"
            style={{
              minHeight: "55vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: "1.25rem", margin: 0 }}>{loadError}</p>
            <button
              type="button"
              onClick={() => setRetryCount((count) => count + 1)}
              style={statusActionStyle}
            >
              Retry
            </button>
          </div>
        ) : players.length === 0 ? (
          <div style={{
            flex: 1, // Take up remaining vertical space
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}>
            <p style={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              textAlign: "center",
              margin: 0
            }}>No scores submitted yet.</p>
          </div>
        ) : (
          players.map((player, i) => {

            const calcGuessPoints = (p) => {
              const eventPoints = p.events ? p.events.reduce((sum, e) => sum + (e.points || 0), 0) : 0;
              return p.score - eventPoints;
            };
            const guessPoints = calcGuessPoints(player);
            const guessText = getGuessCountLabel(player.numGuesses, guessPoints);

            return (
              <React.Fragment key={player.userId || `guest-${player.name}`}>
                <motion.div
                  onClick={() => setExpandedPlayer(expandedPlayer === i ? null : i)}
                  initial={false}
                  animate={{
                    height: expandedPlayer === i ? "auto" : "70px",
                  }}
                  exit={{
                    height: "70px",
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                  style={{
                    overflow: "hidden",
                    background: "#002d21",
                    color: "#e7c17f",
                    borderRadius: "14px",
                    padding: "0",
                    marginBottom: "1rem",
                    fontSize: "1.25rem",
                    fontFamily: "'Baskerville', serif",
                    fontWeight: "bold",
                    cursor: player.events ? "pointer" : "default",
                    transition: "background 0.1s ease",
                  }}
                  whileHover={{ background: "#00372a" }}
                >
                  {/* Top Row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      height: "70px",
                      padding: "0 1.25rem",
                    }}
                  >
                    <div style={{ fontSize: "1.25rem", minWidth: "40px", color: "#e7c17f" }}>{player.rank}</div>
                    <div style={{ flex: 1 }}>{player.name || "Anonymous"}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                      <span style={{ color: "#e7c17f", fontSize: "1.25rem", fontWeight: "bold" }}>{player.score}</span>
                      {player.events && (
                        <span style={{
                          transition: "transform 0.3s ease",
                          transform: expandedPlayer === i ? "rotate(180deg)" : "rotate(0deg)",
                          display: "flex",
                          alignItems: "center",
                          opacity: 1,
                          color: "#e7c17f",
                          width: "20px",
                          textAlign: "center"
                        }}>
                          <ChevronDown />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Animated Breakdown Table */}
                  <motion.div
                    initial={false}
                    animate={{
                      opacity: expandedPlayer === i ? 1 : 0,
                      height: expandedPlayer === i ? "auto" : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={{
                      overflow: "hidden",
                      pointerEvents: expandedPlayer === i ? "auto" : "none",
                    }}
                  >
                    <div style={{ padding: "1rem 0" }}>
                      <table
                        style={{
                          width: "100%",
                          fontSize: "1rem",
                          borderCollapse: "collapse",
                          color: "#e7c17f",
                        }}
                      >
                        <thead>
                          <tr style={{ textAlign: "left", borderBottom: "1px solid #e7c17f" }}>
                            <th className="event-col" style={{ fontWeight: "bold", padding: "0.5rem 1.5rem" }}>Event</th>
                            <th className="guess-col" style={{ fontWeight: "bold", textAlign: "center", padding: "0.5rem 1.5rem" }}>Guess</th>
                            <th className="actual-col" style={{ fontWeight: "bold", textAlign: "center", padding: "0.5rem 1.5rem" }}>Actual</th>
                            <th className="points-col" style={{ fontWeight: "bold", textAlign: "right", padding: "0.5rem 1.5rem" }}>Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {player.events.map((e, idx) => (
                            <tr
                              key={idx}
                              style={{
                                color: "#e7c17f",
                              }}
                            >
                              <td
                                className="event-col" style={{
                                  padding: "0.5rem 1.5rem",
                                  textAlign: "left",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "180px", // 👈 FORCE truncation width
                                }}
                                title={e.event}
                              >
                                {e.event}
                              </td>
                              <td className="guess-col" style={{ padding: "0.5rem 1.5rem", textAlign: "center" }}>{e.guess}</td>
                              <td className="actual-col" style={{ padding: "0.5rem 1.5rem", textAlign: "center" }}>{e.actual}</td>
                              <td className="points-col" style={{ textAlign: "right", padding: "0.75rem 1.5rem", fontWeight: "bold" }}>{e.points}</td>
                            </tr>
                          ))}
                          {/* Guesses Row */}
                          <tr style={{ color: "#e7c17f", borderTop: "none" }}>
                            <td className="event-col" style={{ padding: "0.5rem 1.5rem", textAlign: "left", fontWeight: "bold" }}>
                              {guessText}
                            </td>
                            {/* Empty cells with classes to respect responsive hiding */}
                            <td className="guess-col" style={{ padding: "0.5rem 1.5rem" }}></td>
                            <td className="actual-col" style={{ padding: "0.5rem 1.5rem" }}></td>

                            <td className="points-col" style={{ textAlign: "right", padding: "0.75rem 1.5rem", fontWeight: "bold" }}>
                              {guessPoints}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                </motion.div>
              </React.Fragment>
            );
          })
        )}

      </div>
    </div>
  );
};

const statusActionStyle = {
  width: "min(100%, 280px)",
  minHeight: "3.25rem",
  padding: "0.75rem 1.25rem",
  border: "none",
  borderRadius: "8px",
  background: "linear-gradient(135deg, #002d21, #00372a)",
  color: "#e7c17f",
  fontFamily: "'Baskerville', serif",
  fontSize: "1.15rem",
  fontWeight: "bold",
  cursor: "pointer",
};

export default Leaderboard;
